import * as React from "react";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/utils";
import { Spinner } from "./spinner";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:-translate-y-px hover:opacity-95",
        secondary: "border border-border bg-card text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-muted hover:text-foreground",
        outline: "border border-primary text-primary hover:bg-primary/5",
        danger: "bg-danger text-white hover:-translate-y-px hover:opacity-95"
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 rounded-md px-3",
        lg: "h-11 rounded-md px-6",
        icon: "h-9 w-9 p-0"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);

const Button = React.forwardRef(
  (
    {
      className,
      variant,
      size,
      loading = false,
      loadingText = "",
      asChild = false,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const classes = cn(buttonVariants({ variant, size, className }));
    const content = (
      <>
        {loading ? <Spinner size="sm" className={loadingText ? "mr-2" : ""} /> : null}
        {loading && loadingText ? loadingText : children}
      </>
    );

    if (asChild && React.isValidElement(children)) {
      const slottedContent = (
        <>
          {loading ? <Spinner size="sm" className={loadingText ? "mr-2" : ""} /> : null}
          {loading && loadingText ? loadingText : children.props.children}
        </>
      );
      return React.cloneElement(children, {
        className: cn(classes, children.props.className),
        "aria-disabled": loading || disabled || undefined
      }, slottedContent);
    }

    return (
      <button
        className={classes}
        ref={ref}
        disabled={loading || disabled}
        {...props}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
